import React, { useState, useContext } from "react";
import { GlobalContext } from "../context/Provider";
import henceforthApi from "../utils/henceforthApi";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  Bell,
  Send,
  Mail,
  Smartphone,
  Search,
  X,
  RotateCcw,
  Users,
  Globe,
} from "lucide-react";

import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import "./Notification.scss";

interface notList {
  _id: string;
  email: string;
}
interface notlistUser {
  _id: string;
  name: string;
}
interface notification {
  type: string;
  send_to: any;
  user_ids: any;
  list_user: Array<notlistUser>;
  find_user: Array<notList>;
  selectedUser: any;
  country_filter: string;
  user_type_filter: string;
}

const ReactQuill =
  typeof window === "object" ? require("react-quill") : () => false;

const ALL_USERS = "ALL_USERS";
const SELECTED_USERS = "SELECTED_USERS";
const EMAIL = "EMAIL";
const PUSH = "PUSH";

const COUNTRY_OPTIONS = [
  { id: "All", label: "All Users", flag: "🌐" },
  { id: "US", label: "United States", flag: "🇺🇸" },
  { id: "CA", label: "Canada", flag: "🇨🇦" },
  { id: "NG", label: "Nigeria", flag: "🇳🇬" },
  { id: "IN", label: "India", flag: "🇮🇳" },
  { id: "ROW", label: "Rest of the World", flag: "🌍" },
  { id: "Selected users", label: "Only Selected Users", flag: "👤" },
];

const USER_TYPE_OPTIONS = [
  { id: "All", label: "All" },
  { id: "NOT_VERIFY", label: "NOT VERIFY" },
  { id: "ACCEPTED", label: "ACCEPTED" },
];

export const Notification = () => {
  const { authState } = useContext(GlobalContext);
  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const [description, setDesciption] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [subject, setSubject] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [state, setState] = useState<notification>({
    type: EMAIL,
    send_to: ALL_USERS,
    user_ids: [],
    list_user: [],
    find_user: [],
    selectedUser: new Map(),
    country_filter: "All",
    user_type_filter: "All",
  });

  const radioHandlerType = (type: string) => {
    setState((prev) => ({
      ...prev,
      type,
    }));
    setDesciption("");
  };

  const radioHandlerCountry = (country_filter: string) => {
    const send_to =
      country_filter === "Selected users" ? SELECTED_USERS : ALL_USERS;
    setState((prev) => ({
      ...prev,
      country_filter,
      send_to,
      user_type_filter:
        country_filter === "Selected users" ? "All" : prev.user_type_filter,
    }));
  };

  const radioHandlerUserType = (user_type_filter: string) => {
    setState((prev) => ({
      ...prev,
      user_type_filter,
    }));
  };

  const handleFilter = async (value: string) => {
    setInputValue(value);
    if (value.trim()) {
      try {
        const apiRes = await henceforthApi.Common.allUsers(value.trim());
        setState((prev) => ({
          ...prev,
          find_user: apiRes?.data?.data || [],
        }));
      } catch (error: any) {
        setState((prev) => ({
          ...prev,
          find_user: [],
        }));
      }
    } else {
      setState((prev) => ({
        ...prev,
        find_user: [],
      }));
    }
  };

  const onClickChip = (_id: string, name: string) => {
    const { selectedUser } = state;
    if (selectedUser.has(_id)) {
      selectedUser.delete(_id);
    } else {
      selectedUser.set(_id, name);
    }
    const rowDataId: Array<string> = [];
    const rowData: Array<{ _id: string; name: string }> = [];
    selectedUser.forEach((res: any, key: any) => {
      rowDataId.push(key);
      rowData.push({ _id: key, name: res });
    });
    setState((prev) => ({
      ...prev,
      list_user: rowData,
      selectedUser,
      user_ids: rowDataId,
      find_user: [],
    }));
    setInputValue("");
  };

  const onEditorStateChange = (e: any) => {
    setDesciption(e);
  };

  const handleReset = () => {
    setSubject("");
    setDesciption("");
    setInputValue("");
    setState({
      type: EMAIL,
      send_to: ALL_USERS,
      user_ids: [],
      list_user: [],
      find_user: [],
      selectedUser: new Map(),
      country_filter: "All",
      user_type_filter: "All",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim()) {
      return toast.warn("Please enter subject");
    }
    if (!description.trim()) {
      return toast.warn("Please enter message content");
    }
    if (state.country_filter === "Selected users" && state.user_ids.length === 0) {
      return toast.warn("Please select at least one recipient user");
    }

    const items = {
      type: state.type,
      send_to: state.send_to,
      user_ids: state.user_ids,
      subject: subject.trim(),
      message: description,
      language: "ENGLISH",
      country_filter: state.country_filter,
      user_type_filter: state.user_type_filter,
    };

    try {
      setSubmitting(true);
      const res: any = await henceforthApi.Administrator.sendNotifications(items);
      Swal.fire({
        title: "Notification Sent",
        text: res?.message || "Notification has been successfully sent.",
        icon: "success",
        confirmButtonColor: "#006747",
        confirmButtonText: "OK",
      });
      handleReset();
    } catch (err: any) {
      Swal.fire({
        title: "Error!",
        text:
          err?.response?.body?.error_description ||
          err?.message ||
          "Could not send notification. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc2626",
        confirmButtonText: "Cancel",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animated fadeInRight notification-page">
      <form onSubmit={handleSubmit} className="notification-form">

            {/* Section 1: Notification Channel */}
            <div className="form-section">
              <div className="section-label">
                <Bell className="w-3.5 h-3.5" />
                <span>Notification Channel</span>
              </div>
              <div className="toggle-group">
                <button
                  type="button"
                  className={`toggle-btn ${state.type === EMAIL ? "active" : ""}`}
                  onClick={() => radioHandlerType(EMAIL)}
                >
                  <Mail className="w-3.5 h-3.5" />
                  <span>Email</span>
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${state.type === PUSH ? "active" : ""}`}
                  onClick={() => radioHandlerType(PUSH)}
                >
                  <Smartphone className="w-3.5 h-3.5" />
                  <span>Push Notification</span>
                </button>
              </div>
            </div>

            {/* Section 2: Select Users by Country */}
            <div className="form-section">
              <div className="section-label">
                <Globe className="w-3.5 h-3.5" />
                <span>Select Recipients by Country</span>
              </div>
              <div className="country-chips">
                {COUNTRY_OPTIONS.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`country-chip ${state.country_filter === c.id ? "active" : ""}`}
                    onClick={() => radioHandlerCountry(c.id)}
                  >
                    <span className="chip-flag">{c.flag}</span>
                    <span className="chip-label">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Section 3: User Type Filter (hidden when "Selected users") */}
            {state.country_filter !== "Selected users" && (
              <div className="form-section">
                <div className="section-label">
                  <Users className="w-3.5 h-3.5" />
                  <span>User Type Filter</span>
                </div>
                <div className="toggle-group">
                  {USER_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`toggle-btn ${state.user_type_filter === opt.id ? "active" : ""}`}
                      onClick={() => radioHandlerUserType(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Section 4: Select Users by Email (when "Selected users") */}
            {state.country_filter === "Selected users" && (
              <div className="form-section">
                <div className="section-label-row">
                  <div className="section-label">
                    <Users className="w-3.5 h-3.5" />
                    <span>Select Users by Email</span>
                  </div>
                  {state.list_user.length > 0 && (
                    <span className="selected-count">
                      {state.list_user.length} selected
                    </span>
                  )}
                </div>

                <div className="user-search-box">
                  <div className="search-input-wrap">
                    <Search className="search-ico" />
                    <Input
                      type="text"
                      className="search-input"
                      placeholder="Search user by email address..."
                      value={inputValue}
                      onChange={(e) => handleFilter(e.target.value)}
                    />
                  </div>

                  {state.find_user.length > 0 && (
                    <div className="user-dropdown">
                      {state.find_user.map((res: any) => {
                        const isSelected = state.selectedUser.has(res._id);
                        return (
                          <button
                            key={res._id}
                            type="button"
                            className={`user-dropdown-item ${isSelected ? "selected" : ""}`}
                            onClick={() => onClickChip(res._id, `${res.email}`)}
                          >
                            <span className="user-email">{res.email}</span>
                            {isSelected ? (
                              <span className="badge-selected">✓ Selected</span>
                            ) : (
                              <span className="badge-add">+ Add</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {state.list_user.length > 0 && (
                  <div className="selected-users-chips">
                    {state.list_user.map((res) => (
                      <span key={res._id} className="user-chip">
                        <span className="user-chip-name">{res.name}</span>
                        <button
                          type="button"
                          className="user-chip-remove"
                          onClick={() => onClickChip(res._id, res.name)}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Section 5: Subject */}
            <div className="form-section">
              <label className="field-label">
                Subject <span className="required">*</span>
              </label>
              <Input
                name="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter notification subject"
                className="field-input"
              />
            </div>

            {/* Section 6: Message Content */}
            <div className="form-section">
              <label className="field-label">
                Message Content <span className="required">*</span>
              </label>
              {state.type === EMAIL ? (
                <div className="quill-wrapper">
                  <ReactQuill
                    theme="snow"
                    onChange={onEditorStateChange}
                    placeholder="Write your email content..."
                    value={description}
                  />
                </div>
              ) : (
                <textarea
                  rows={5}
                  value={description}
                  onChange={(e) => setDesciption(e.target.value)}
                  placeholder="Write your push notification message..."
                  className="push-textarea"
                />
              )}
            </div>

            {/* Section 7: Action Buttons */}
            <div className="form-actions">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleReset}
                className="action-btn-reset"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reset</span>
              </Button>

              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={
                  submitting ||
                  !subject.trim() ||
                  !description.trim() ||
                  (state.country_filter === "Selected users" && state.user_ids.length === 0)
                }
                className="action-btn-send"
              >
                {submitting ? (
                  <>
                    <i className="fa fa-spinner fa-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    <span>Send Notification</span>
                  </>
                )}
              </Button>
            </div>

          </form>
    </div>
  );
};

export default Notification;
